import { useMutation, useQuery } from '@apollo/client';
import { useMelwaysInfo } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';
import { useToast } from 'braid-design-system';
import { useCallback } from 'react';

import type {
  GetApproachabilityQuery,
  GetProfileVisibilityQuery,
  UpdateApproachabilityMutation,
  UpdateProfileVisibilityMutation,
} from 'src/types/graphql';

import translations from './.vocab';
import {
  getProfileVisibility,
  updateProfileVisibilityMutation,
} from './legacy-visibility.graphql';
import {
  getApproachability,
  updateApproachabilityMutation,
} from './visibility.graphql';

/*
Talent Search visibility:
*/
export const useGetProfileVisibility = () => {
  const { language } = useMelwaysInfo();
  const { data, loading, error } = useQuery<GetProfileVisibilityQuery>(
    getProfileVisibility,
    {
      variables: {
        languageCode: language,
      },
    },
  );

  const profileVisibilityLevel =
    loading || error ? null : data?.viewer?.profileVisibility?.level || null;
  const profileVisibilityDescription =
    loading || error
      ? null
      : data?.viewer?.profileVisibility?.description || null;

  return {
    profileVisibilityLevel,
    profileVisibilityDescription,
    loading,
    error,
  };
};

export const useUpdateProfileVisibility = () => {
  const showToast = useToast();
  const { t } = useTranslations(translations);

  const onCompleted = useCallback(() => {
    showToast({
      message: t('Profile visibility updated'),
      tone: 'positive',
      key: 'profile-visibility',
    });
  }, [showToast, t]);

  const onError = useCallback(() => {
    showToast({
      message: t('Profile visibility could not be updated'),
      tone: 'critical',
      key: 'profile-visibility',
    });
  }, [showToast, t]);

  const [updateProfileVisibility, { data, loading, error }] =
    useMutation<UpdateProfileVisibilityMutation>(
      updateProfileVisibilityMutation,
      {
        onCompleted,
        onError,
      },
    );

  return { updateProfileVisibility, data, loading, error };
};

export const useGetApproachability = () => {
  const { data, loading, error } =
    useQuery<GetApproachabilityQuery>(getApproachability);

  const approachable = data?.viewer?.approachability?.approachable ?? true;

  return {
    approachable,
    loading,
    error,
  };
};

export const useUpdateApproachability = () => {
  const showToast = useToast();
  const { t } = useTranslations(translations);

  const onCompleted = useCallback(() => {
    showToast({
      message: t('Profile visibility updated'),
      tone: 'positive',
      key: 'profile-visibility',
    });
  }, [showToast, t]);

  const onError = useCallback(() => {
    showToast({
      message: t('Profile visibility could not be updated'),
      tone: 'critical',
      key: 'profile-visibility',
    });
  }, [showToast, t]);

  const [updateApproachability, { data, loading, error }] =
    useMutation<UpdateApproachabilityMutation>(updateApproachabilityMutation, {
      onCompleted,
      onError,
    });

  return { updateApproachability, data, loading, error };
};
